'use client'

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
    FormField,
    FormItem,
    FormLabel,
    FormControl,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { HelpCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Label } from "@/components/ui/label";
import { Form } from '@/components/ui/form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useCreateRequestItemMutation, useUpdateRequestItemMutation } from '@/redux/slices/stack-and-ship/stack-and-ship-apiSlice';
import { useStepperContext } from '@/pages/distribution-analytics/components/stepper-provider';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setIsPending } from '@/redux/slices/stack-and-ship/stack-and-ship-slice';
import {
    DISTRIBUTION_ITEMS_PER_CASE_OPTIONS,
    DISTRIBUTION_PRIMARY_PACKAGE_TYPE_OPTIONS,
} from '@/lib/packageOptions';
interface ProjectDetailsProps {
    form: any;
}

const brandOptions = [
    "CSD",
    "Lipton",
    "Gatorade",
    "Aquafina",
    "Propel",
    "Mtn Dew",
    "CSD/Water",
    "Pepsi",
    "PureLeaf",
    "SoBe",
    "Tropicana",
    "Naked",
    "Starbucks",
    "Frutly",
    "KeVita",
    "Rockstar",
    "Izze",
    "Dr Pepper",
    "LifeWtr"
];

const applicationOptions = [
    "Ambient",
    "Aseptic",
    "Cold Fill",
    "ESL",
    "gel",
    "Hot Fill",
    "powder",
    "Retort",
    "tunnel pasteurization",
    "CSD",
    "NCB Non-Pressurized",
    "NCB Pressurized",
];

const packageTypeOptions = DISTRIBUTION_PRIMARY_PACKAGE_TYPE_OPTIONS;

const customerOptions = [
    "Retail Chain",
    "Wholesale",
    "Direct to Consumer",
    "Other"
];

const packPatternOptions = DISTRIBUTION_ITEMS_PER_CASE_OPTIONS;
const mlUnits = ['oz', 'ml']
const inchUnits = ["inch", "mm"]
const bottleDesignOptions = [
    "Global Swirl",
    "Prius",
    "McCartney",
    "Citrus",
    "4 Bolt",
    "Azera",
    "Ecofina",
    "Ecofina - Gen2",
    "Propel",
    "Sidekick",
    "Ripples",
    "Axl",
    "Skinnee",
    "Straight Wall",
    "Vuitton",
    "Wolverine",
    "Live Big",
    "Lizardade",
    "Kenny",
    "Gatorade",
    "Tropicana",
    "LIT Chilled",
    "Naked",
    "TPP Carafe",
    "Aquafina",
    "ColdBrew",
    "Polar",
    "Rho",
    "Active",
    "Kevita",
    "LifeWtr"
];

const palletBaseTypeOptions = [
    // Wood Pallets
    "48×40×5 Wood",
    "42×36×5 Wood",
    "48×36×5 Wood",
    "48×40×5.5/5.6 Wood",
    "48×48×5 Wood",
    "56×48×5 Wood",
    "48×37×5 Wood",
    "48×41×5 Wood",
    "48×43×5 Wood",
    "48×44×6 Wood",
    "36×36×5 Wood",
    "48×42×5 Wood",
    "37×37×5 Wood",
    "43×37×5 Wood",
    "43×36×5 Wood",
    "47×35×5 Wood",
    "47×36×5 Wood",
    "48×40×5.18 Wood",
    "48×44×5 Wood",

    // Plastic Pallets
    "37×37×5 Plastic",
    "48×36×5 Plastic",
    "36×36×5 Plastic",
    "48×42×5 Plastic",
    "56×48×5 Plastic",
    "43×37×5 Plastic",
    "42×36×5 Plastic",
    "48×48×5 Plastic",
    "43×36×5 Plastic",
    "47×35×5 Plastic",
    "47×36×5 Plastic",
    "48×40×5 Plastic",
    "48×40×5.5/5.6 Plastic",
    "48×37×5 Plastic",
    "48×41×5 Plastic",
    "48×43×5 Plastic",
    "48×44×5 Plastic",
    "48×44×6 Plastic",
];


const categoryOptions = [
    {
        label: "CSD", value: "CSD"
    },
    {
        label: "S&F", value: "SF"
    },
    {
        label: "Tea", value: "Tea"
    },
    {
        label: "Hydration", value: "Hydration"
    },
    {
        label: "juice plus", value: "juice plus"
    },
    {
        label: "Coffee Partnership", value: "Coffee Partnership"
    }
]

const formSchema = z.object({
    // Project Details
    project_name: z.string().min(1, 'Project name is required'),
    brand_name: z.string().min(1, 'Brand name is required'),
    location_name: z.string().min(1, 'Location is required'),
    customer: z.string().min(1, 'Customer is required'),
    application_name: z.string().min(1, 'Application is required'),
    primary_package_type: z.string().min(1, 'Package type is required'),
    has_tertiary_package: z.string().min(1, 'Please select yes or no'),
    category: z.string().min(1, 'Category is required'),

    // Package Details
    net_contents: z.string().min(1, 'Net contents must be positive'),
    net_contents_unit: z.string(),
    filled_top_load: z.nullable(z.string()),
    filled_top_load_unit: z.nullable(z.string()),
    primary_package_diameter: z.string().min(1, 'Diameter must be positive'),
    primary_package_diameter_unit: z.string(),
    primary_package_height: z.string().min(1, 'Height must be positive'),
    primary_package_height_unit: z.string(),
    bottle_design: z.string().nullable().optional(),
    pack_per_case: z.string().nullable().optional(),
    items_per_case: z.string().min(1, 'Items per case is required'),
    pallet_base_type: z.string(),
}).superRefine((data, ctx) => {
    // Conditional validation for bottle_design
    if (data.primary_package_type === 'pet_bottles' && (!data.bottle_design || data.bottle_design.trim() === '')) {
        ctx.addIssue({
            path: ['bottle_design'],
            message: 'Bottle design is required when primary package type is PET Bottles',
            code: z.ZodIssueCode.custom,
        });
    }

    // Conditional validation for pack_per_case
    if (data.has_tertiary_package === 'yes' && (!data.pack_per_case || data.pack_per_case.trim() === '')) {
        ctx.addIssue({
            path: ['pack_per_case'],
            message: 'Pack per case is required when has tertiary package is yes',
            code: z.ZodIssueCode.custom,
        });
    }
});
const initialValues = {
    project_name: '',
    brand_name: '',
    location_name: '',
    customer: '',
    category: '',
    bottle_design: '',
    application_name: '',
    primary_package_type: '',
    has_tertiary_package: 'no',
    net_contents: '',
    net_contents_unit: 'oz',
    filled_top_load: '',
    filled_top_load_unit: 'lbf',
    primary_package_diameter: '',
    primary_package_diameter_unit: 'inch',
    primary_package_height: '',
    primary_package_height_unit: 'inch',
    pack_per_case: '',
    items_per_case: "",
    pallet_base_type: ""
}
const ProjectDetails: React.FC = ({ requestItem, refetch }) => {
    const [createRequestItem, { isLoading, }] = useCreateRequestItemMutation();
    const [updateRequestItem, { isLoading: isUpdating, }] = useUpdateRequestItemMutation();
    const { handleNext } = useStepperContext();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [hasLoaded, setHasLoaded] = useState(false);
    const [initialFormValues, setInitialFormValues] = useState<any>(null);
    const [hasFormChanged, setHasFormChanged] = useState(false);

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            ...initialValues, ...Object.keys(initialValues).reduce((acc, key) => {
                if (requestItem && key in requestItem) {
                    acc[key] = requestItem[key];
                }
                return acc;
            }, {})
        },
        mode: "onChange",
    });

    // Save initial form values after initial load
    useEffect(() => {
        if (!hasLoaded) {
            const currentValues = form.getValues();
            setInitialFormValues(currentValues);
            setHasLoaded(true);
        }
    }, [form, hasLoaded]);

    // Subscribe to form changes to detect modifications
    useEffect(() => {
        const subscription = form.watch((value) => {
            if (hasLoaded && initialFormValues) {
                // Compare current form values with initial values
                const currentValues = form.getValues();
                const hasChanged = Object.keys(currentValues).some(key => {
                    // Handle empty strings vs null/undefined correctly
                    const initialValue = initialFormValues[key] || '';
                    const currentValue = currentValues[key] || '';
                    return initialValue !== currentValue;
                });

                setHasFormChanged(hasChanged);
            }
        });

        return () => subscription.unsubscribe();
    }, [form, hasLoaded, initialFormValues]);

    // Helper function to remove null and undefined values
    const cleanData = (obj: any) => {
        const cleaned: any = {};
        Object.keys(obj).forEach(key => {
            if (obj[key] === null || obj[key] === undefined || obj[key] === 'undefined') {
                cleaned[key] = obj[key];
            }else{
                cleaned[key] = obj[key];
            }
        });
        return cleaned;
    };

    const onSubmit = async (data: z.infer<typeof formSchema>) => {
        // Only proceed if the form has changes or it's a new item (no requestItem.id)
        if (hasFormChanged || !requestItem?.id) {
            try {
                // Clean data to remove null, undefined, and empty string values
                const cleanedData = cleanData(data);

                if (requestItem?.id) {
                    console.log(cleanedData, 'cleaned data for update')
                    const response = await updateRequestItem({ data: cleanedData, id: requestItem.id });
                    await refetch?.();
                    // Update initial values after successful submission
                    setInitialFormValues(form.getValues());
                    setHasFormChanged(false);
                } else {
                    console.log(cleanedData, 'cleaned data for create')
                    const response = await createRequestItem(cleanedData);
                    navigate(`/distribution-analytics/view-project/${response?.data?.id}?newProject=yes`);
                }
                handleNext();
            } catch (error) {
                console.error('Error submitting form:', error);
            }
        } else {
            // No changes, just proceed to next step
            handleNext();
        }
    };

    const onError = (errors: any) => {
        console.log('Form errors:', errors);
    };

    const hasTertiaryPackage = form.watch('has_tertiary_package');
    console.log(hasTertiaryPackage, 'hasTertiaryPackage')

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit, onError)} className="space-y-6 max-w-7xl mx-auto">
                <div className='overflow-y-scroll space-y-6'>
                    {Object.keys(form.formState.errors).length > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-4 mx-4">
                            <h3 className="text-red-800 font-medium mb-2">Please fix the following errors:</h3>
                            <ul className="list-disc pl-5 space-y-1">
                                {Object.entries(form.formState.errors).map(([key, error]) => (
                                    <li key={key} className="text-red-600 text-sm">
                                        {error?.message as string}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Project Details Section */}
                    <div>
                        {/* Header Row */}
                        <div className="flex-center border-border-style-1 border w-full">
                            <div className="label-bg">
                                <Label className="label font-bold">Project Details</Label>
                            </div>
                        </div>

                        {/* First Row of Inputs */}
                        <div className="flex-center border-border-style-1 border w-full">
                            <div className="relative w-full">
                                <div className="p-2 flex flex-col">
                                    <Label className="pb-2 label font-bold">Project Name*</Label>
                                    <FormField
                                        control={form.control}
                                        name="project_name"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <Input className=' border-required border-2' placeholder="Enter project name" {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="relative w-full">
                                <div className="p-2 flex flex-col">
                                    <Label className="pb-2 label font-bold">Brand*</Label>
                                    <FormField
                                        control={form.control}
                                        name="brand_name"
                                        render={({ field }) => {
                                            console.log(field, 'brand_name field')
                                            return (
                                                <FormItem>
                                                    <Select
                                                        onValueChange={field.onChange}
                                                        defaultValue={field.value}
                                                    >
                                                        <FormControl>
                                                            <SelectTrigger className="w-full border-required border-2 bg-selectBlue">
                                                                <SelectValue placeholder="Select brand" />
                                                            </SelectTrigger>
                                                        </FormControl>
                                                        <SelectContent>
                                                            {brandOptions.map((brand) => (
                                                                <SelectItem key={brand} value={brand}>
                                                                    {brand}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    <FormMessage />
                                                </FormItem>
                                            )
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="relative w-full">
                                <div className="p-2 flex flex-col">
                                    <Label className=" pb-2 label font-bold">Location*</Label>
                                    <FormField
                                        control={form.control}
                                        name="location_name"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <Input className=' border-required border-2' placeholder="Enter location" {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="relative w-full">
                                <div className="p-2 flex flex-col">
                                    <Label className=" pb-2 label font-bold">Customer*</Label>
                                    <FormField
                                        control={form.control}
                                        name="customer"
                                        render={({ field }) => (
                                            <FormItem>
                                                <Select
                                                    onValueChange={field.onChange}
                                                    defaultValue={field.value}
                                                >
                                                    <FormControl>
                                                        <SelectTrigger className="w-full border-required border-2 bg-selectBlue">
                                                            <SelectValue placeholder="Select customer" />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        {customerOptions.map((customer) => (
                                                            <SelectItem key={customer} value={customer}>
                                                                {customer}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Second Row of Inputs */}
                        <div className="flex-center border-border-style-1 border w-full">
                            <div className="relative w-full">
                                <div className="p-2 flex flex-col">
                                    <Label className=" pb-2 label font-bold">Application*</Label>
                                    <FormField
                                        control={form.control}
                                        name="application_name"
                                        render={({ field }) => (
                                            <FormItem>
                                                <Select
                                                    onValueChange={field.onChange}
                                                    defaultValue={field.value}
                                                >
                                                    <FormControl>
                                                        <SelectTrigger className="w-full border-required border-2 bg-selectBlue">
                                                            <SelectValue placeholder="Select application" />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        {applicationOptions.map((app) => (
                                                            <SelectItem key={app} value={app}>
                                                                {app}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="relative w-full">
                                <div className="p-2 flex flex-col">
                                    <Label className=" pb-2 label font-bold">Primary Package Type*</Label>
                                    <FormField
                                        control={form.control}
                                        name="primary_package_type"
                                        render={({ field }) => (
                                            <FormItem>
                                                <Select
                                                    onValueChange={field.onChange}
                                                    defaultValue={field.value}
                                                >
                                                    <FormControl>
                                                        <SelectTrigger className="w-full border-required border-2 bg-selectBlue">
                                                            <SelectValue placeholder="Select package type" />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        {packageTypeOptions.map((option) => (
                                                            <SelectItem key={option.value} value={option.value}>
                                                                {option.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="relative w-full">
                                <div className="p-2 flex flex-col">
                                    <Label className=" pb-2 label font-bold">Category*</Label>
                                    <FormField
                                        control={form.control}
                                        name="category"
                                        render={({ field }) => (
                                            <FormItem>
                                                <Select
                                                    onValueChange={field.onChange}
                                                    defaultValue={field.value}
                                                >
                                                    <FormControl>
                                                        <SelectTrigger className="w-full border-required border-2 bg-selectBlue">
                                                            <SelectValue placeholder="Select category" />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        {categoryOptions.map((option) => (
                                                            <SelectItem key={option.value} value={option.value}>
                                                                {option.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="relative w-full"></div>
                            <div className="relative w-full"></div>
                        </div>
                    </div>

                    {/* Package Details Section */}
                    <div className="mt-6">
                        {/* Header Row */}
                        <div className="flex-center border-border-style-1 border w-full">
                            <div className="label-bg">
                                <Label className="  label font-bold">Package Details</Label>
                            </div>
                        </div>

                        {/* First Row of Inputs */}
                        <div className="flex-center border-border-style-1 border w-full">
                            <div className="relative w-full">
                                <div className="p-2 flex flex-col">
                                    <Label className=" pb-2 label font-bold">Net Contents*</Label>
                                    <div className="flex gap-2">
                                        <FormField
                                            control={form.control}
                                            name="net_contents"
                                            render={({ field }) => (
                                                <FormItem className="flex-1">
                                                    <FormControl>
                                                        <Input type="number" className='border-required border-2' placeholder="Enter value" {...field} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <Select
                                            name="net_contents_unit"
                                            value={form.watch('net_contents_unit')}
                                            onValueChange={(value) => form.setValue('net_contents_unit', value)}
                                        >
                                            <SelectTrigger className=" bg-unitSelect rounded-[11px] py-3 w-[70px] h-[38px] text-xs  text-white">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {
                                                    mlUnits.map((unit) => (
                                                        <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                                                    ))
                                                }
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>

                            <div className="relative w-full">
                                <div className="p-2 flex flex-col">
                                    <Label className=" pb-2 label font-bold">Filled Top Load</Label>
                                    <div className="flex gap-2">
                                        <FormField
                                            control={form.control}
                                            name="filled_top_load"
                                            render={({ field }) => (
                                                <FormItem className="flex-1">
                                                    <FormControl>
                                                        <Input type="number" placeholder="Enter value" {...field} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <Select
                                            name="filled_top_load_unit"
                                            value={form.watch('filled_top_load_unit')}
                                            onValueChange={(value) => form.setValue('filled_top_load_unit', value)}
                                        >
                                            <SelectTrigger className=" bg-unitSelect rounded-[11px] py-3 w-[70px] h-[38px] text-xs  text-white">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="lbf">lbf</SelectItem>
                                                <SelectItem value="kgf">kgf</SelectItem>
                                                <SelectItem value="N">N</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>

                            <div className="relative w-full">
                                <div className="p-2 flex flex-col">
                                    <Label className=" pb-2 label font-bold">Primary Package Diameter*</Label>
                                    <div className="flex gap-2">
                                        <FormField
                                            control={form.control}
                                            name="primary_package_diameter"
                                            render={({ field }) => (
                                                <FormItem className="flex-1">
                                                    <FormControl>
                                                        <Input type="number" className='border-required border-2' placeholder="Enter value" {...field} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <Select
                                            name="primary_package_diameter_unit"
                                            value={form.watch('primary_package_diameter_unit')}
                                            onValueChange={(value) => form.setValue('primary_package_diameter_unit', value)}
                                        >
                                            <SelectTrigger className=" bg-unitSelect rounded-[11px] py-3 w-[70px] h-[38px] text-xs  text-white">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {
                                                    inchUnits.map((unit) => (
                                                        <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                                                    ))
                                                }
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>

                            <div className="relative w-full">
                                <div className="p-2 flex flex-col">
                                    <Label className=" pb-2 label font-bold">Primary Package Height*</Label>
                                    <div className="flex gap-2">
                                        <FormField
                                            control={form.control}
                                            name="primary_package_height"
                                            render={({ field }) => (
                                                <FormItem className="flex-1">
                                                    <FormControl>
                                                        <Input type="number" className='border-required border-2' placeholder="Enter value" {...field} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <Select
                                            name="primary_package_height_unit"
                                            value={form.watch('primary_package_height_unit')}
                                            onValueChange={(value) => form.setValue('primary_package_height_unit', value)}
                                        >
                                            <SelectTrigger className=" bg-unitSelect rounded-[11px] py-3 w-[70px] h-[38px] text-xs  text-white">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {
                                                    inchUnits.map((unit) => (
                                                        <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                                                    ))
                                                }
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Bottle Design Row (Conditional) */}
                        {form.watch('primary_package_type') === 'pet_bottles' && (
                            <div className="flex-center border-border-style-1 border w-full">
                                <div className="relative w-full">
                                    <div className="flex flex-col p-2">
                                        <Label className=" pb-2 label font-bold">Bottle Design*</Label>
                                        <FormField
                                            control={form.control}
                                            name="bottle_design"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <Select
                                                        onValueChange={field.onChange}
                                                        defaultValue={field.value}
                                                    >
                                                        <FormControl>
                                                            <SelectTrigger className="w-full border-required border-2 bg-selectBlue">
                                                                <SelectValue placeholder="Select bottle design" />
                                                            </SelectTrigger>
                                                        </FormControl>
                                                        <SelectContent>
                                                            {bottleDesignOptions.map((design) => (
                                                                <SelectItem key={design} value={design}>
                                                                    {design}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                </div>
                                <div className="relative w-full"></div>
                                <div className="relative w-full"></div>
                                <div className="relative w-full"></div>
                            </div>
                        )}
                    </div>

                    {/* Shipper Details Section */}
                    <div className="mt-6">
                        {/* Header Row */}
                        <div className="flex-center border-border-style-1 border w-full">
                            <div className="label-bg">
                                <Label className="  label font-bold">Shipper Details</Label>
                            </div>
                        </div>

                        {/* Tertiary Package Question */}
                        <div className="flex-center border-border-style-1 border w-full">
                            <div className="relative w-full p-4">
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2">
                                        <Label className=" pb-2 label font-bold text-nowrap">Do you have a tertiary package?*</Label>
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger>
                                                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>Help button shows a picture illustrating shippers with (pack and case) and without (case only) tertiary package.</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                    <FormField
                                        control={form.control}
                                        name="has_tertiary_package"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <RadioGroup
                                                        onValueChange={field.onChange}
                                                        defaultValue={field.value}
                                                        className="flex gap-4"
                                                    >
                                                        <div className="flex items-center space-x-2">
                                                            <RadioGroupItem value="yes" id="yes" />
                                                            <label htmlFor="yes">Yes</label>
                                                        </div>
                                                        <div className="flex items-center space-x-2">
                                                            <RadioGroupItem value="no" id="no" />
                                                            <label htmlFor="no">No</label>
                                                        </div>
                                                    </RadioGroup>
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    {hasTertiaryPackage === 'yes' ? (
                                        <div className="flex-center gap-10 w-full">
                                            <div className="relative w-full">
                                                <div className="flex flex-col">
                                                    <Label className=" pb-2 label font-bold">Items / Case *</Label>
                                                    <FormField
                                                        control={form.control}
                                                        name="items_per_case"
                                                        render={({ field }) => (
                                                            <FormItem>
                                                                <Select
                                                                    onValueChange={field.onChange}
                                                                    defaultValue={field.value}
                                                                >
                                                                    <FormControl>
                                                                        <SelectTrigger className="w-full bg-selectBlue">
                                                                            <SelectValue placeholder="Select pack pattern" />
                                                                        </SelectTrigger>
                                                                    </FormControl>
                                                                    <SelectContent>
                                                                        {packPatternOptions.map((pattern) => (
                                                                            <SelectItem key={pattern.id} value={pattern.value}>
                                                                                {pattern.value}
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                                <FormMessage />
                                                            </FormItem>
                                                        )}
                                                    />
                                                </div>
                                            </div>

                                            <div className="relative w-full">
                                                <div className="flex flex-col">
                                                    <Label className=" pb-2 label font-bold">Pack / Case</Label>
                                                    <FormField
                                                        control={form.control}
                                                        name="pack_per_case"
                                                        render={({ field }) => (
                                                            <FormItem>
                                                                <Select
                                                                    onValueChange={field.onChange}
                                                                    defaultValue={field.value}
                                                                >
                                                                    <FormControl>
                                                                        <SelectTrigger className="w-full bg-selectBlue">
                                                                            <SelectValue placeholder="Select pack/case" />
                                                                        </SelectTrigger>
                                                                    </FormControl>
                                                                    <SelectContent>
                                                                        {packPatternOptions.map((pattern) => (
                                                                            <SelectItem key={pattern.id} value={pattern.value}>
                                                                                {pattern.value}
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                                <FormMessage />
                                                            </FormItem>
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                            <div className="relative w-full"></div>
                                            <div className="relative w-full"></div>
                                        </div>
                                    ) : (
                                        <div className="flex-center  w-full">
                                            <div className="relative w-full">
                                                <div className="flex flex-col">
                                                    <Label className=" pb-2 label font-bold">Items / Case</Label>
                                                    <FormField
                                                        control={form.control}
                                                        name="items_per_case"
                                                        render={({ field }) => (
                                                            <FormItem>
                                                                <Select
                                                                    onValueChange={field.onChange}
                                                                    defaultValue={field.value}
                                                                >
                                                                    <FormControl>
                                                                        <SelectTrigger className="w-full bg-selectBlue">
                                                                            <SelectValue placeholder="Select items/case" />
                                                                        </SelectTrigger>
                                                                    </FormControl>
                                                                    <SelectContent>
                                                                        {packPatternOptions.map((pattern) => (
                                                                            <SelectItem key={pattern.id} value={pattern.value}>
                                                                                {pattern.value}
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                                <FormMessage />
                                                            </FormItem>
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                            <div className="relative w-full"></div>
                                            <div className="relative w-full"></div>
                                            <div className="relative w-full"></div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Pallet Details Section */}
                    <div className="mt-6 pb-10">
                        {/* Header Row */}
                        <div className="flex-center border-border-style-1 border w-full">
                            <div className="label-bg">
                                <Label className="  label font-bold">Pallet Details</Label>
                            </div>
                        </div>

                        {/* Pallet Base Type */}
                        <div className="flex-center border-border-style-1 border w-full">
                            <div className="relative w-full">
                                <div className="p-2 flex flex-col">
                                    <Label className=" pb-2 label font-bold">Pallet Base Type*</Label>
                                    <FormField
                                        control={form.control}
                                        name="pallet_base_type"
                                        render={({ field }) => (
                                            <FormItem>
                                                <Select
                                                    onValueChange={field.onChange}
                                                    defaultValue={field.value}
                                                >
                                                    <FormControl>
                                                        <SelectTrigger className="w-full border-required border-2 bg-selectBlue">
                                                            <SelectValue placeholder="Select pallet base type" />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        {palletBaseTypeOptions.map((type) => (
                                                            <SelectItem key={type} value={type}>
                                                                {type}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="relative w-full"></div>
                            <div className="relative w-full"></div>
                            <div className="relative w-full"></div>
                        </div>
                    </div>
                    <button
                        id='submit-button'
                        type="submit"
                        style={{ display: 'none' }}
                        title="Submit form"
                    />
                </div>
            </form>
        </Form>
    );
};

export default ProjectDetails; 
